import { useState } from 'react';
import { ChevronDown, Loader2, Upload } from 'lucide-react';
import { bulkAddAgencies } from '@generated/server/agencies';
import { getStaffPass } from '@generated/hooks/staffPass';

const SOURCES: { title: string; how: string }[] = [
  { title: 'Ask the agencies you pitch', how: 'When someone replies, ask who buys local/community print and for their mailing address. Click the pencil to save it.' },
  { title: 'New York Press Service & NNA', how: 'Ask NYPS (nynewspapers.com) and the National Newspaper Association which agencies place ads in member papers. They often share buyer lists with members.' },
  { title: 'Your own past advertisers', how: 'Look at old ads that came in through an agency (camera-ready art, agency invoices) and add the agency\'s contact from the invoice.' },
  { title: 'Political campaign filings', how: 'NYC Campaign Finance Board (nyccfb.info) and NY State BOE spending reports list the media firms candidates pay, with mailing addresses.' },
  { title: 'City & nonprofit advertisers', how: 'The Mayor\'s Office of Ethnic & Community Media, plus hospitals, colleges and utilities, place notices in community papers. Ask for their agency of record.' },
  { title: 'LinkedIn & agency websites', how: 'Search "media buyer" or "print buyer" + New York. Agency "Contact" pages usually list the office mailing address.' },
];

/** Paste lines like: Agency, Contact, email, Address (address may contain commas). */
function parse(text: string) {
  return text.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
    const parts = (l.includes('\t') ? l.split('\t') : l.split(',')).map((p) => p.trim());
    const [name = '', contactName = '', email = '', ...rest] = parts;
    return { name, contactName, email, address: rest.join(', ') };
  }).filter((r) => r.name);
}

export function GrowList({ onAdded }: { onAdded: (msg: string) => void }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const rows = parse(text);

  async function add() {
    setBusy(true); setErr('');
    try {
      const r = await bulkAddAgencies({ data: { pass: getStaffPass(), rows } });
      setText('');
      onAdded(`Added ${r.added} agenc${r.added === 1 ? 'y' : 'ies'}${r.skipped ? `, skipped ${r.skipped} already on the list` : ''}.`);
    } catch (e) { console.error(e); setErr('Could not add those. Check the format and try again.'); }
    finally { setBusy(false); }
  }

  return (
    <div className="border-2 border-foreground bg-[hsl(var(--brand-yellow)/0.35)]">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex min-h-12 w-full items-center justify-between px-4 text-left">
        <span><span className="font-bold">Grow the list</span><span className="ml-2 text-sm text-muted-foreground">Where to find more agencies, names & mailing addresses</span></span>
        <ChevronDown className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="grid gap-6 border-t-2 border-foreground bg-card p-4 lg:grid-cols-2">
          <ol className="space-y-3">
            {SOURCES.map((s, i) => (
              <li key={s.title} className="flex gap-3">
                <span className="font-[family-name:var(--font-heading)] text-2xl font-bold leading-none">{i + 1}</span>
                <div><p className="font-semibold">{s.title}</p><p className="text-sm text-muted-foreground">{s.how}</p></div>
              </li>
            ))}
            <li className="border-l-4 border-primary pl-3 text-sm">Tip: for each agency, aim for a <b>person's name</b>, an email, and a <b>mailing address</b>. Agencies with addresses can later get a printed media kit or postcard through Select Mail.</li>
          </ol>
          <div>
            <p className="font-semibold">Paste a list</p>
            <p className="text-sm text-muted-foreground">One agency per line: <b>Agency, Contact name, Email, Mailing address</b>. Copying rows straight from a spreadsheet works too. Only the agency name is required. Names already on the list are skipped.</p>
            <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} className="mt-2 w-full border-2 border-foreground bg-background p-2 font-mono text-xs"
              placeholder={'Acme Media, Jane Doe, jane@acme.com, 100 Main St, New York, NY 10001\nBlue Door Agency, , info@bluedoor.com,'} />
            {err && <p className="text-sm text-destructive">{err}</p>}
            <button onClick={add} disabled={busy || rows.length === 0} className="mt-2 inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}Add {rows.length || ''} to list
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
